/**
 * 极简 IndexedDB 封装（PRD §10/§12）。
 *
 * 不引入外部 IDB 库；直接基于浏览器原生 `indexedDB` API。提供：
 *   - openDatabase(name, version, stores)
 *   - getStore(name) / put / get / delete / list / clear
 *   - withTransaction(storeNames, mode, fn)
 *
 * 该模块支持 happy-dom 测试：在测试中可以通过 `setIndexedDB(mock)` 注入一个
 * 最小 fake 实现。
 */

export type StoreMode = 'readonly' | 'readwrite';

/** 对象仓库定义 */
export interface StoreDefinition {
  /** 仓库名 */
  name: string;
  /** 主键 path（默认 'id'） */
  keyPath?: string;
  /** 索引定义列表 */
  indexes?: Array<{ name: string; keyPath: string | string[]; unique?: boolean }>;
}

/** 数据库名（与 PRD §12 一一对应） */
export const PROLOGUE_DB_NAME = 'prologue';
/** 数据库 schema 版本号 */
export const PROLOGUE_DB_VERSION = 1;

/** 全部对象仓库定义（顺序敏感，决定 IDB 内 store 的物理顺序） */
export const PROLOGUE_STORES: StoreDefinition[] = [
  { name: 'accounts', keyPath: 'id' },
  { name: 'pairSpaces', keyPath: 'id' },
  { name: 'memberKeys', keyPath: 'id' },
  { name: 'photoMemories', keyPath: 'id' },
  { name: 'photoAttachments', keyPath: 'id' },
  { name: 'letters', keyPath: 'id' },
  { name: 'drafts', keyPath: 'id' },
  { name: 'meetStories', keyPath: 'id' },
  { name: 'deleteRequests', keyPath: 'id' },
  { name: 'recycleItems', keyPath: 'id' },
  { name: 'syncOperations', keyPath: 'id' },
  { name: 'exportRequests', keyPath: 'id' },
];

/** 数据库连接 */
export interface DbConnection {
  /** 原始 IDBDatabase */
  raw: IdbDatabaseLike;
  /** 关闭 */
  close: () => void;
}

/** 兼容 fake IDBDatabase 的最小接口（不依赖 DOM lib） */
export interface IdbDatabaseLike {
  name: string;
  version: number;
  objectStoreNames: { contains(n: string): boolean };
  transaction(names: string[], mode: 'readonly' | 'readwrite'): IdbTransactionLike;
  createObjectStore(name: string, opts: { keyPath?: string }): IdbObjectStoreLike;
  close(): void;
}

/** 兼容 fake IDBObjectStore 的最小接口 */
export interface IdbObjectStoreLike {
  name: string;
  keyPath: string;
  put(value: Record<string, unknown>): { onsuccess: ((ev: unknown) => void) | null; onerror: ((ev: unknown) => void) | null; error: unknown };
  get(id: string): { onsuccess: ((ev: unknown) => void) | null; onerror: ((ev: unknown) => void) | null; result: unknown; error: unknown };
  getAll(): { onsuccess: ((ev: unknown) => void) | null; onerror: ((ev: unknown) => void) | null; result: readonly unknown[] | undefined; error: unknown };
  delete(id: string): { onsuccess: ((ev: unknown) => void) | null; onerror: ((ev: unknown) => void) | null; error: unknown };
  clear(): { onsuccess: ((ev: unknown) => void) | null; onerror: ((ev: unknown) => void) | null; error: unknown };
  createIndex?(name: string, keyPath: string | string[], opts?: { unique?: boolean }): unknown;
}

/** 兼容 fake IDBTransaction 的最小接口 */
export interface IdbTransactionLike {
  objectStore(name: string): IdbObjectStoreLike;
  oncomplete: (() => void) | null;
  onerror: ((err: unknown) => void) | null;
  onabort: ((err: unknown) => void) | null;
}

/** 全局 IDB 注入钩子（便于测试替换） */
interface IndexedDBLike {
  open(name: string, version?: number): {
    result: IdbDatabaseLike | undefined;
    error: unknown;
    onsuccess: ((ev: unknown) => void) | null;
    onerror: ((ev: unknown) => void) | null;
    onupgradeneeded: ((ev: unknown) => void) | null;
    onblocked: (() => void) | null;
  };
  __isFake?: boolean;
  [k: string]: unknown;
}

let injectedIndexedDB: IndexedDBLike | null = null;

/** 注入 fake indexedDB（仅用于测试）。 */
export function setIndexedDB(factory: IndexedDBLike): void {
  injectedIndexedDB = factory;
}

/** 当前生效的 indexedDB（优先 injected；否则 globalThis）。 */
function getIndexedDB(): IndexedDBLike {
  if (injectedIndexedDB) return injectedIndexedDB;
  if (typeof globalThis !== 'undefined' && (globalThis as { indexedDB?: unknown }).indexedDB) {
    return (globalThis as unknown as { indexedDB: IndexedDBLike }).indexedDB;
  }
  throw new Error('storage.db: indexedDB is unavailable (call setIndexedDB in tests)');
}

/** 内部缓存：name → DbConnection */
const dbCache = new Map<string, { version: number; promise: Promise<DbConnection> }>();

/**
 * 打开（创建）数据库。
 *
 * @param name    数据库名
 * @param version 数据库版本号
 * @param stores  期望的 object store 定义；新版本会按需 upgrade
 */
export function openDatabase(
  name: string,
  version: number,
  stores: StoreDefinition[],
): Promise<DbConnection> {
  const cached = dbCache.get(name);
  if (cached && cached.version === version) {
    return cached.promise;
  }

  const promise = new Promise<DbConnection>((resolve, reject) => {
    const req = getIndexedDB().open(name, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db) return;
      for (const def of stores) {
        if (!db.objectStoreNames.contains(def.name)) {
          const os = db.createObjectStore(def.name, {
            keyPath: def.keyPath ?? 'id',
          });
          for (const idx of def.indexes ?? []) {
            os.createIndex?.(idx.name, idx.keyPath, { unique: idx.unique ?? false });
          }
        }
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      if (!db) {
        reject(new Error('openDatabase: result is null'));
        return;
      }
      resolve({
        raw: db,
        close: () => db.close(),
      });
    };
    req.onerror = () => reject(req.error ?? new Error('openDatabase failed'));
    req.onblocked = () => reject(new Error('openDatabase blocked by other connection'));
  });

  dbCache.set(name, { version, promise });
  return promise;
}

/** 关闭 + 清除缓存（用于测试间重置）。 */
export function resetDatabaseCache(): void {
  for (const entry of dbCache.values()) {
    entry.promise.then((conn) => conn.close()).catch(() => {});
  }
  dbCache.clear();
}

/** 拿一个 object store（自动开库）。 */
export async function getStore(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeName: string,
  mode: StoreMode = 'readonly',
): Promise<IdbObjectStoreLike> {
  const conn = await openDatabase(dbName, dbVersion, stores);
  const tx = conn.raw.transaction([storeName], mode);
  return tx.objectStore(storeName);
}

/** 写入或更新记录。 */
export async function put<T extends { id: string }>(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeName: string,
  value: T,
): Promise<string> {
  const store = await getStore(dbName, dbVersion, stores, storeName, 'readwrite');
  return new Promise<string>((resolve, reject) => {
    const req = store.put(value);
    req.onsuccess = () => resolve(value.id);
    req.onerror = () => reject(req.error ?? new Error('put failed'));
  });
}

/** 按主键读取。 */
export async function get<T>(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeName: string,
  id: string,
): Promise<T | undefined> {
  const store = await getStore(dbName, dbVersion, stores, storeName, 'readonly');
  return new Promise<T | undefined>((resolve, reject) => {
    const req = store.get(id);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error ?? new Error('get failed'));
  });
}

/** 按主键删除。 */
export async function remove(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeName: string,
  id: string,
): Promise<void> {
  const store = await getStore(dbName, dbVersion, stores, storeName, 'readwrite');
  await new Promise<void>((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject((req as unknown as { error: unknown }).error ?? new Error('delete failed'));
  });
}

/** 列出全部记录。 */
export async function list<T>(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeName: string,
): Promise<T[]> {
  const store = await getStore(dbName, dbVersion, stores, storeName, 'readonly');
  return new Promise<T[]>((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(((req as unknown as { result: T[] }).result) ?? []);
    req.onerror = () => reject((req as unknown as { error: unknown }).error ?? new Error('list failed'));
  });
}

/** 清空一个 store。 */
export async function clear(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeName: string,
): Promise<void> {
  const store = await getStore(dbName, dbVersion, stores, storeName, 'readwrite');
  await new Promise<void>((resolve, reject) => {
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject((req as unknown as { error: unknown }).error ?? new Error('clear failed'));
  });
}

/**
 * 跨 store 事务（如删除一个对象时同时写一个 RecycleItem + SyncOperation）。
 *
 * @returns fn 的返回值；事务在 fn 返回后才完成。
 */
export async function withTransaction<T>(
  dbName: string,
  dbVersion: number,
  stores: StoreDefinition[],
  storeNames: string[],
  mode: StoreMode,
  fn: (tx: IdbTransactionLike) => Promise<T> | T,
): Promise<T> {
  const conn = await openDatabase(dbName, dbVersion, stores);
  const tx = conn.raw.transaction(storeNames, mode);
  const result = await fn(tx);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject((tx as unknown as { error: Error | null }).error ?? new Error('transaction failed'));
    tx.onabort = () => reject((tx as unknown as { error: Error | null }).error ?? new Error('transaction aborted'));
  });
  return result;
}
